////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// turn-widget-music-form.ts — the GM's music form.
// ----------------------------------------------------------------------------------------------------
// Renders INLINE into #div_refresh_gm_viewer, the same pane the light/weather forms use — not a modal.
//
// Themes come from the music CATALOG the page already holds (window.ftMusicThemeRows, music-player.ts):
// one row per bucket, no request of their own. Until 2026-09-10 my_campaign.php emitted
// window.FT_MUSIC_THEMES from a whole-table GROUP BY over `audio` on every campaign page load; that
// island is gone (kept only as a read fallback for a stale page).
//
// THREE STATES, in priority order — the form only ever sets the top two:
//   LOCAL      toggle on + a theme picked -> only this GM hears it. Writes nothing, broadcasts
//              nothing (music-player.ts holds it in sessionStorage).
//   BROADCAST  toggle off + a theme picked -> everyone hears it (campaign_encounter_mx_bucket,
//              pushed on the existing campaign_update frame).
//   AUTO       neither set -> the encounter's own ambient theme, swapping to its combat theme when
//              initiative is rolled and back when combat ends. "Back to auto" clears both, which is
//              why the override is a nullable pointer rather than another mode to keep in sync.
//
// The old Everyone/Just-me pair of buttons per row is gone: the toggle says which mode you are in,
// so a theme needs exactly one play button.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const FT_MXF_HOST = 'div_refresh_gm_viewer';
const FT_MXF_LOCAL_MODE_KEY = 'ft_mx_local_mode';

interface FtMusicThemeRow {
    bucket: string;
    vocal?: boolean;
    count?: number;
    minutes?: number;
}

function _ftMxfWin() {
    return window as unknown as {
        BASE_URL?: string;
        FT_MUSIC_THEMES?: FtMusicThemeRow[];
        ftGetCampaignId?: () => string;
        ftMusicSetLocal?: (b: string) => void;
        ftMusicClearLocal?: () => void;
        ftMusicLocalBucket?: () => string | null;
        ftMusicPause?: (ms?: number) => void;
        ftMusicResume?: () => void;
        ftMusicIsPaused?: () => boolean;
        ftMusicCurrentBucket?: () => string | null;
        ftSyncViewerMusic?: () => Promise<void>;
        ftMusicAdoptCampaignOverride?: (bucket: string, campaignId?: string, token?: unknown) => boolean;
        ftMusicCampaignToken?: (campaignId?: string) => unknown;
        ftMusicThemeRows?: () => Promise<FtMusicThemeRow[]>;
    };
}

function _ftMxfEsc(s: unknown): string {
    return String(s ?? '').replace(/[&<>"']/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c
    ));
}

/** Local MODE is remembered even with no theme picked, so the toggle keeps its position. */
function _ftMxfLocalMode(): boolean {
    try { return sessionStorage.getItem(FT_MXF_LOCAL_MODE_KEY) === '1'; }
    catch (e) { return false; }
}
function _ftMxfSetLocalMode(on: boolean): void {
    try { sessionStorage.setItem(FT_MXF_LOCAL_MODE_KEY, on ? '1' : '0'); }
    catch (e) { /* private mode — mode just won't survive a reload */ }
}

/**
 * Feedback goes to the house warning banner (displayWarning -> #div_sidelog_warning), not to a
 * strip inside the form. Same channel every other action on this page reports through, and it
 * keeps the form itself to controls only.
 */
function _ftMxfStatus(msg: string): void {
    const w = window as unknown as { displayWarning?: (m: string) => void };
    if (typeof w.displayWarning === 'function') w.displayWarning(msg);
}

async function _ftMxfPost(payload: Record<string, unknown>): Promise<Record<string, unknown> | null> {
    const base = String(_ftMxfWin().BASE_URL || '');
    try {
        const res = await fetch(base + 'options/game/audio/set_campaign_music_inc.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        const text = (await res.text()).trim();
        try { return JSON.parse(text) as Record<string, unknown>; }
        catch (e) { return { ok: false, error: 'Bad response: ' + text.slice(0, 120) }; }
    } catch (e) {
        return { ok: false, error: String(e) };
    }
}

/**
 * Campaign id, using the same fallback chain the side panels use rather than only
 * window.ftGetCampaignId — the sidelog context is populated on some pages where the global
 * resolves empty, and an empty id silently turns every broadcast action into a no-op.
 */
function _ftMxfCampaignId(): string {
    const w = window as unknown as {
        ftGetCampaignId?: () => string;
        getSidelogContext?: () => { campaign_id?: unknown } | null;
        ftGetEncounterContext?: () => { campaign_id?: unknown } | null;
    };
    const ctx = (typeof w.ftGetEncounterContext === 'function' ? w.ftGetEncounterContext() : null)
        || (typeof w.getSidelogContext === 'function' ? w.getSidelogContext() : null)
        || null;
    return String(
        (ctx && ctx.campaign_id) ||
        (typeof w.ftGetCampaignId === 'function' ? w.ftGetCampaignId() : '') ||
        '',
    ).trim();
}

/** Display form of a bucket name: battle_epic_god_mode -> "Battle Epic God Mode". */
function _ftMxfLabel(bucket: string): string {
    const w = window as unknown as { ucwordsAndSpace?: (s: string) => string };
    if (typeof w.ucwordsAndSpace === 'function') return w.ucwordsAndSpace(bucket);
    return String(bucket || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Theme rows once the catalog has answered; null until then. The state separates "still loading" from
// "failed / empty" so the list never says Loading… forever (Codex review 2026-09-10).
let _ftMxfThemeRows: FtMusicThemeRow[] | null = null;
let _ftMxfThemeState: 'idle' | 'loading' | 'loaded' | 'failed' = 'idle';

function _ftMxfThemes(): FtMusicThemeRow[] {
    if (_ftMxfThemeRows) return _ftMxfThemeRows;
    const t = _ftMxfWin().FT_MUSIC_THEMES;
    return Array.isArray(t) ? t : [];
}

/** Load the theme rows from the catalog once (a failure retries on the next open), then repaint. */
function _ftMxfLoadThemes(): void {
    if (_ftMxfThemeRows || _ftMxfThemeState === 'loading') return;
    const load = _ftMxfWin().ftMusicThemeRows;
    if (typeof load !== 'function') { _ftMxfThemeState = 'failed'; return; }
    _ftMxfThemeState = 'loading';
    void load()
        .then((rows) => {
            if (Array.isArray(rows) && rows.length) { _ftMxfThemeRows = rows; _ftMxfThemeState = 'loaded'; }
            else _ftMxfThemeState = 'failed';   // no catalog (or an empty one)
        })
        .catch(() => { _ftMxfThemeState = 'failed'; })
        .finally(() => { _ftMxfRefresh(); });
}

function _ftMxfRenderList(filter: string): void {
    const host = document.getElementById('ft_mxf_list');
    if (!host) return;
    const q = filter.trim().toLowerCase();
    const active = _ftMxfWin().ftMusicCurrentBucket?.() || _ftMxfWin().ftMusicLocalBucket?.() || '';
    const rows = _ftMxfThemes().filter((t) => !q
        || t.bucket.toLowerCase().indexOf(q) !== -1
        || _ftMxfLabel(t.bucket).toLowerCase().indexOf(q) !== -1);

    if (!rows.length) {
        host.innerHTML = _ftMxfThemes().length
            ? '<div class="text-muted small p-2">No theme matches “' + _ftMxfEsc(filter) + '”.</div>'
            : _ftMxfThemeState === 'failed'
                ? '<div class="text-muted small p-2">Music themes are unavailable right now — reopen Music to retry.</div>'
                : '<div class="text-muted small p-2">Loading themes…</div>';
        return;
    }

    const paused = _ftMxfWin().ftMusicIsPaused?.() ?? false;
    const iconPlay = typeof iconMap === 'function' ? iconMap('play') : '&#9654;';
    const iconPause = typeof iconMap === 'function' ? iconMap('pause') : '&#10073;&#10073;';

    host.innerHTML = rows.map((t) => {
        // "Active" = the bucket actually loaded in the player, whatever mode put it there.
        const isActive = !!active && t.bucket === active;
        // Active row toggles to pause; the row IS the transport for what it is playing.
        const btnIcon = (isActive && !paused) ? iconPause : iconPlay;
        const btnAttr = (isActive && !paused)
            ? 'data-ft-mxf-pause="1"'
            : 'data-ft-mxf-play="' + _ftMxfEsc(t.bucket) + '"';
        const label = (isActive && !paused) ? 'Pause' : 'Play ' + _ftMxfLabel(t.bucket);
        // Row separators, the active wash and the button size live in CSS (.ft-mxf-row, 90-after.css):
        // a hard border-bottom per row inside a bordered list inside a bordered window was the
        // "border hell" of 2026-09-24.
        return ''
            + '<div class="ft-mxf-row d-flex align-items-center' + (isActive ? ' ft-mxf-active' : '') + '">'
            +   '<button type="button" class="btn btn-sm btn-primary ft-mxf-play" ' + btnAttr
            +      ' aria-label="' + _ftMxfEsc(label) + '" title="' + _ftMxfEsc(label) + '">' + btnIcon + '</button>'
            // ms-2 + text-start: the name sits immediately left-aligned beside the button.
            +   '<div class="flex-grow-1 min-w-0 ms-2 text-start">'
            +     '<span class="text-truncate">' + _ftMxfEsc(_ftMxfLabel(t.bucket)) + '</span>'
            +     (t.vocal ? ' <span class="badge text-bg-info">vocal</span>' : '')
            +   '</div>'
            +   '<span class="small text-muted text-nowrap ms-2">' + (t.count ?? 0) + ' tracks'
            +     (t.minutes ? ' · ' + t.minutes + ' min' : '') + '</span>'
            + '</div>';
    }).join('');
}

function _ftMxfRenderModeLine(): void {
    const el = document.getElementById('ft_mxf_mode');
    if (!el) return;
    if (_ftMxfWin().ftMusicIsPaused?.()) {
        el.innerHTML = 'Paused — press Resume to bring the music back.';
        return;
    }
    // Deliberately BLANK in plain auto mode: the default state needs no caption, and a
    // permanent "Auto - ..." line was just noise above the list.
    const local = _ftMxfWin().ftMusicLocalBucket?.() || null;
    el.innerHTML = local
        ? 'Playing <strong>' + _ftMxfEsc(local) + '</strong> for you only.'
        : '';
}

/**
 * Repaint playback state only while Music is the active GM workspace tool. A playlist change is
 * ambient state, not navigation: if Inventory, Weather, encounter notes, or another tool owns the
 * shared viewer, it must remain there until the GM explicitly presses the Music button again.
 */
function _ftMxfRefresh(): void {
    const host = document.getElementById(FT_MXF_HOST);
    if (!host) return;
    if (!document.getElementById('div_encounter_music_form')) return;
    _ftMxfRenderModeLine();
    _ftMxfRenderList((document.getElementById('ft_mxf_search') as HTMLInputElement | null)?.value || '');
    const paused = _ftMxfWin().ftMusicIsPaused?.() ?? false;
    const btn = document.getElementById('ft_mxf_pause_btn');
    if (btn) {
        btn.innerHTML = paused
            ? (typeof iconMap === 'function' ? iconMap('play') : '&#9654;')
            : (typeof iconMap === 'function' ? iconMap('pause') : '&#10073;&#10073;');
        btn.setAttribute('title', paused ? 'Resume' : 'Pause');
        btn.setAttribute('aria-label', paused ? 'Resume music' : 'Pause music');
    }
}

/** Render the music form into the GM viewer pane. */
function ftOpenMusicForm(): void {
    const host = document.getElementById(FT_MXF_HOST);
    if (!host) return;

    const localMode = _ftMxfLocalMode();
    const paused = _ftMxfWin().ftMusicIsPaused?.() ?? false;
    const icon = typeof iconMap === 'function' ? iconMap('music') : '';
    const iconPause = typeof iconMap === 'function' ? iconMap('pause') : '&#10073;&#10073;';
    const iconPlay  = typeof iconMap === 'function' ? iconMap('play')  : '&#9654;';

    // Trailing spyglass/clear, mirroring includes/search_inline_trailing_icons_inc.php (which we
    // cannot include from a client-rendered form). Deliberately NO data-ft-inline-search on the
    // wrapper: that attribute hands the box to the generic filter-search-init delegation, which
    // would POST. This form owns its own search, exactly like the catalog-list mounts do.
    const searchIcons = `
            <span class="ft-inline-search-trailing position-absolute top-50 end-0 translate-middle-y me-2 d-flex align-items-center justify-content-center">
                <button type="button" class="ft-inline-search-clear-btn d-none" data-ft-inline-search-role="clear" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-animation="false" data-bs-original-title="Clear" aria-label="Clear search">
                    <span aria-hidden="true" style="line-height:0"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true" focusable="false" class="ft-inline-search-trailing-svg"><path d="M18 6L6 18M6 6l12 12"/></svg></span>
                </button>
                <span class="ft-inline-search-glyph" data-ft-inline-search-role="glyph" aria-hidden="true" style="line-height:0">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true" focusable="false" class="ft-inline-search-trailing-svg"><circle cx="11" cy="11" r="7"/><path d="M16 16l4.5 4.5"/></svg>
                </span>
            </span>`;

    host.innerHTML = `
<!-- data-ft3d-workspace-*: the FT3D Workspace bar shows "Music" + icon in place of "Workspace",
     so the in-form title below is hidden there (CSS) and the toolbar collapses to controls only. -->
<div class="ft-mxf-form row gx-0" id="div_encounter_music_form"
     data-ft3d-workspace-title="Music" data-ft3d-workspace-icon="music">
    <div class="ft-mxf-form__col col-12 p-2">

        <!-- Header on the catalog-list toolbar shape (ft-cat-toolbar): identity on the left,
             search + actions on the right, one row on desktop and stacked on mobile. Same
             structure as view_audio_files.php et al. -->
        <div class="ft-cat-toolbar d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3 mb-2 p-2">

            <div class="ft-mxf-identity d-flex align-items-center gap-3 me-md-auto">
                <h3 class="ft-cat-title h5 m-0 d-flex align-items-center gap-2">${icon}<span>Music</span></h3>

                <!-- House checkbox markup (render_checkbox in functions.php): the
                     form-check + d-flex align-items-center pair is what actually centres the
                     box against its label. Plain checkbox, never a form-switch. -->
                <div class="form-check d-flex align-items-center m-0">
                    <input class="form-check-input me-2 mt-0" type="checkbox"
                           id="ft_mxf_local_toggle" ${localMode ? 'checked' : ''}>
                    <label class="form-check-label small text-nowrap" for="ft_mxf_local_toggle">
                        Local only <span class="text-muted">(just me)</span>
                    </label>
                </div>
            </div>

            <!-- Grows into the toolbar like the catalog pages' search, but capped so it stops
                 looking like a URL bar on a wide screen. 18rem was too cramped to read a theme
                 name back. -->
            <div class="ft-cat-search d-flex align-items-center gap-2 flex-grow-1">
                <div class="position-relative flex-grow-1 min-w-0 ft-inline-search-root"
                     style="min-width:18rem;max-width:34rem">
                    <label class="visually-hidden" for="ft_mxf_search">Search themes</label>
                    <input id="ft_mxf_search" type="search" class="form-control form-control-sm w-100 pe-5"
                           placeholder="Search themes (battle, tavern, forest…)" autocomplete="off">
                    ${searchIcons}
                </div>

                <!-- transport + reset together, per feedback -->
                <button type="button" class="btn btn-sm btn-outline-secondary border rounded ft-mxf-transport" data-ft-mxf-pause="1"
                        id="ft_mxf_pause_btn" title="${paused ? 'Resume' : 'Pause'}"
                        aria-label="${paused ? 'Resume music' : 'Pause music'}">${paused ? iconPlay : iconPause}</button>
                <button type="button" class="btn btn-sm btn-outline-warning text-nowrap" data-ft-mxf-auto="1">Back to auto</button>
            </div>
        </div>

        <!-- Only survivor of the old status block: says what is playing LOCALLY, and stays
             empty in the ordinary auto case. Everything else reports via displayWarning. -->
        <div id="ft_mxf_mode" class="ft-mxf-mode small text-muted mb-2"></div>

        <!-- Height is CSS-owned: 16rem in a stacked pane, the whole remaining frame in the FT3D
             Workspace window (.ft-mxf-list, 90-after.css). -->
        <div id="ft_mxf_list" class="ft-mxf-list"></div>
    </div>
</div>`;

    _ftMxfRenderModeLine();
    _ftMxfRenderList('');
    _ftMxfSyncTrailing();
    _ftMxfLoadThemes();
}

/** Swap spyglass <-> clear based on whether the box has text (house behaviour). */
function _ftMxfSyncTrailing(): void {
    const input = document.getElementById('ft_mxf_search') as HTMLInputElement | null;
    if (!input) return;
    const box = input.closest('.ft-inline-search-root');
    const glyph = box?.querySelector('[data-ft-inline-search-role="glyph"]') ?? null;
    const clear = box?.querySelector('[data-ft-inline-search-role="clear"]') ?? null;
    if (!glyph || !clear) return;
    const hasText = input.value.trim().length > 0;
    glyph.classList.toggle('d-none', hasText);
    clear.classList.toggle('d-none', !hasText);
}

// ── delegated wiring (attached once; the form is re-rendered wholesale) ──────────────────────
document.addEventListener('click', (ev) => {
    const t = ev.target as HTMLElement | null;
    if (!t || typeof t.closest !== 'function') return;

    // Open from the GM controls row (or anywhere else that wants it).
    if (t.closest('[data-ft-music-action="open-form"]')) {
        ev.preventDefault();
        ftOpenMusicForm();
        return;
    }

    const playBtn = t.closest('[data-ft-mxf-play]') as HTMLElement | null;
    if (playBtn) {
        const bucket = playBtn.getAttribute('data-ft-mxf-play') || '';
        if (!bucket) return;

        if (_ftMxfLocalMode()) {
            _ftMxfWin().ftMusicSetLocal?.(bucket);
            _ftMxfStatus('Playing “' + _ftMxfLabel(bucket) + '” for you only.');
            return;
        }

        const campaignId = _ftMxfCampaignId();
        if (!campaignId) { _ftMxfStatus('No campaign in context.'); return; }
        _ftMxfStatus('Pushing “' + bucket + '” to everyone…');
        // The cached blob as the write leaves: adoption below only applies if no push replaced it since.
        const setToken = _ftMxfWin().ftMusicCampaignToken?.(campaignId);
        void _ftMxfPost({ action: 'set', campaign_id: campaignId, bucket }).then((r) => {
            if (r && r.ok) {
                // Music resolves client-side from the campaign cache, which only learns of this write
                // when the campaign_update echo lands. Adopt the confirmed write now, so the re-resolve
                // below plays the NEW theme instead of restarting the old one.
                _ftMxfWin().ftMusicAdoptCampaignOverride?.(bucket, campaignId, setToken);
                // A leftover local override would keep the GM on their own music and make the
                // push look like it failed.
                _ftMxfWin().ftMusicClearLocal?.();
                // Choosing a theme explicitly means play it, just like a local selection.
                // Resume clears this GM's pause and re-resolves the saved campaign theme;
                // background campaign updates still respect each listener's own pause.
                _ftMxfWin().ftMusicResume?.();
                _ftMxfStatus('Everyone is hearing “' + _ftMxfLabel(bucket) + '”.');
            } else {
                _ftMxfStatus(String((r && r.error) || 'Failed to set music.'));
            }
        });
        return;
    }

    // Clear (X) inside the search box.
    if (t.closest('[data-ft-inline-search-role="clear"]')) {
        const input = document.getElementById('ft_mxf_search') as HTMLInputElement | null;
        if (input) { input.value = ''; input.focus(); }
        _ftMxfSyncTrailing();
        _ftMxfRenderList('');
        return;
    }

    if (t.closest('[data-ft-mxf-pause]')) {
        const w = _ftMxfWin();
        if (w.ftMusicIsPaused?.()) {
            w.ftMusicResume?.();
            _ftMxfStatus('Resumed.');
        } else {
            w.ftMusicPause?.();     // fades out rather than cutting
            _ftMxfStatus('Fading out…');
        }
        _ftMxfRefresh();
        return;
    }

    if (t.closest('[data-ft-mxf-auto]')) {
        // Back to AUTO = drop BOTH overrides. Local first (instant, client-side), then the
        // campaign one; a non-GM simply has nothing to clear server-side.
        _ftMxfWin().ftMusicClearLocal?.();
        _ftMxfRefresh();
        const campaignId = _ftMxfCampaignId();
        if (!campaignId) {
            void _ftMxfWin().ftSyncViewerMusic?.();
            _ftMxfStatus('Back to your encounter’s own music.');
            return;
        }
        const clearToken = _ftMxfWin().ftMusicCampaignToken?.(campaignId);
        void _ftMxfPost({ action: 'clear', campaign_id: campaignId }).then((r) => {
            // Same reason as above — adopt the confirmed clear and re-resolve now instead of waiting
            // on the WS echo, otherwise "Back to auto" clears the override server-side and this tab
            // keeps playing the old thing, which reads as the button doing nothing.
            if (r && r.ok) _ftMxfWin().ftMusicAdoptCampaignOverride?.('', campaignId, clearToken);
            void _ftMxfWin().ftSyncViewerMusic?.();
            _ftMxfStatus(r && r.ok
                ? 'Back to auto — each encounter’s own theme resumes.'
                : String((r && r.error) || 'Cleared locally only.'));
        });
    }
});

// The player announces when a playlist actually starts/stops. Repaint off THAT rather than a
// setTimeout guess, which fired before the async resolve had loaded the new playlist and left
// the highlight one selection behind.
document.addEventListener('ft-music-changed', () => { _ftMxfRefresh(); });

document.addEventListener('input', (ev) => {
    const t = ev.target as HTMLInputElement | null;
    if (t && t.id === 'ft_mxf_search') { _ftMxfSyncTrailing(); _ftMxfRenderList(t.value); }
});

document.addEventListener('change', (ev) => {
    const t = ev.target as HTMLInputElement | null;
    if (!t || t.id !== 'ft_mxf_local_toggle') return;
    _ftMxfSetLocalMode(!!t.checked);
    // Turning local OFF drops any local override, so the GM immediately rejoins whatever the
    // party is hearing rather than staying silently on their own track.
    if (!t.checked) _ftMxfWin().ftMusicClearLocal?.();
    _ftMxfRefresh();
    _ftMxfStatus(t.checked
        ? 'Local mode on — themes you pick play only for you.'
        : 'Local mode off — themes you pick play for everyone.');
});

export { ftOpenMusicForm };
